import React, { useState, useMemo } from 'react'
import { Grid, Typography } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import useMediaQuery from '@material-ui/core/useMediaQuery'
//import { Field } from 'redux-form'
import { withTranslation, WithTranslation, Trans } from 'react-i18next'
import { MuiClasses } from '../../types' // adjust path if different
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { useHistory, useRouteMatch } from 'react-router-dom'
import ApplicationsApi from '../../services/applications'
import DigivakuusFaqLink from '../../components/Digivakuus/DigivakuusFaqLink'
import { numberToEuroString } from '../../libs/currencies'

type Props = WithTranslation & {
    classes: MuiClasses
}

const banks = [
    //{ id: 'SANDBOXFINANCE_SFIN0000', name: 'Sandbox', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/sandbox.svg' },
    { id: 'AKTIA_HELSFIHH', name: 'Aktia Bank', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/logo_aktia.svg.png' },
    { id: 'NORWEGIAN_FI_NORWNOK1', name: 'Norwegian', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/bank-norwegian.svg' },
    { id: 'DANSKEBANK_DABAFIHH', name: 'Danske Bank', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/danske-bank.svg' },
    { id: 'HANDELSBANKEN_HANDFIHH', name: 'Handelsbanken', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/handelsbanken.svg' },
    { id: 'N26_NTSBDEB1', name: 'N26 Bank', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/n26.svg' },
    { id: 'NORDEA_NDEAFIHH', name: 'Nordea', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/nordea.svg' },
    { id: 'OMASP_OMSAFI2S', name: 'Oma', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/oma-saastopankki.svg' },
    { id: 'OP_OKOYFIHH', name: 'OP', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/osuuspankki.svg' },
    { id: 'POPPANKKI_POPFFI22', name: 'POP Pankki', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/pop-pankki.svg' },
    { id: 'REVOLUT_REVOLT21', name: 'Revolut', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/revolut.svg' },
    { id: 'S_PANKKI_SBANFIHH', name: 'S-Pankki', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/s-pankki.svg' },
    { id: 'SEB_KORT_AB_FI_SKHSFI21', name: 'SEB', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/seb.svg' },
    //{ id: 'SOLDO_SFSDIE22', name: 'Soldo', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/soldo.svg' },
    { id: 'SAASTOPANKKI_ITELFIHH', name: 'Säästöpankki', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/saastopankki.svg' },
    { id: 'WISE_TRWIGB22', name: 'Wise', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/wise.svg' },
    { id: 'ALANDSBANKEN_AABAFI22', name: 'Ålandsbanken', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/alandsbanken.svg' },
    //DELETE THIS
    //{ id: 'SANDBOXFINANCE_SFIN0000', name: 'asd', logo: 'https://rentapp-images.s3.eu-central-1.amazonaws.com/bank-logos/alandsbanken.svg' }
]

const useLocalStyles = makeStyles(theme => ({
    bankGrid: {
        display: 'grid',
        width: '100%',
        margin: '0 auto',
        gridColumnGap: 12,
        gridRowGap: 12,
        gridTemplateColumns: 'repeat(3, 1fr)',
        '@media (max-width:340px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
        [theme.breakpoints.up('md')]: {
            gridColumnGap: 24,
            gridRowGap: 24,
            gridTemplateColumns: 'repeat(4, 1fr)',
        },
        [theme.breakpoints.up('lg')]: { gridTemplateColumns: 'repeat(5, 1fr)' },
    },

    bankCard: {
        width: '100%',
        height: 57.06,                 // mobile card height
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 8,                    // give the logo room to breathe
        background: '#FFFFFF',
        border: '1px solid #C3C6D1',
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'box-shadow .2s ease, transform .06s ease, border-color .2s ease',
        '&:not(:disabled):hover': { borderColor: '#9fa3b3', boxShadow: '0 8px 24px rgba(16, 24, 40, 0.08)' },
        '&:not(:disabled):active': { transform: 'translateY(1px)' },
        '&:disabled': {
            cursor: 'not-allowed',
            boxShadow: 'none',
        },
        '&:disabled $bankLogo': {
            opacity: 0.45,
            filter: 'grayscale(90%)',
        },
        [theme.breakpoints.up('md')]: {
            height: 96,                  // desktop card height
            borderRadius: 12,
            padding: 12,
        },
    },

    // let logos scale by width first, but cap height so tall icons don't overflow
    bankLogo: {
        display: 'block',
        maxWidth: '80%',      // slightly smaller than before
        height: 'auto',
        maxHeight: 26,        // smaller logos on mobile
        objectFit: 'contain',
        [theme.breakpoints.up('md')]: {
            maxWidth: '80%',             // desktop
            maxHeight: 56,               // matches ~60px visual without clipping
        },
    },

    infoBox: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '30px 24px',
        gap: 20,
        background: '#FBF6EE',
        borderRadius: 20,
        //minHeight: 108,
        marginBottom: 20,
    },

    // fixed-width column for the checkbox so text never flows underneath it
    infoCheckCol: {
        flex: '0 0 24px',
        width: 24,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
    },

    nativeCheckbox: {
        width: 24,
        height: 24,
        boxSizing: 'border-box',
        background: '#FFFFFF',
        border: '1px solid #000000',
        borderRadius: 5,
        marginTop: 5,
        display: 'inline-block',
        margin: 0,
        padding: 0,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        position: 'relative',
        cursor: 'pointer',

        // 🔇 remove browser focus visuals
        outline: 'none !important',
        boxShadow: 'none !important',
        '&:focus, &:focus-visible, &:active': {
            outline: 'none !important',
            boxShadow: 'none !important',
        },
        // mobile tap highlight
        WebkitTapHighlightColor: 'transparent',

        // keep the checkmark invisible by default
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 7,
            top: 1,
            width: 6,
            height: 14,
            border: '3px solid transparent',
            borderTop: 0,
            borderLeft: 0,
            transform: 'rotate(45deg)',
        },

        // ✅ checked = green box + white checkmark
        '&:checked': {
            background: '#46816C',
            borderColor: '#46816C',
        },
        '&:checked::after': {
            borderColor: '#FFFFFF',
        },
    },


    // right side (text + logo) stays in a row; text can wrap but never under the checkbox
    infoRight: {
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 20,
        minWidth: 0,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',     // stack text and logo on mobile
            alignItems: 'flex-start',
            gap: 12,
        },
    },
    infoTextWrap: {
        flex: 1,
        minWidth: 0,
    },

    infoText: {
        fontFamily: '"Circular Std", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontStyle: 'normal',
        fontWeight: 400 as any,
        fontSize: 18,
        lineHeight: '28px',
        color: '#000000',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    infoTextBoldRow: {
        display: 'flex',
        alignItems: 'center',
    },

    infoTextSuffixRow: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'flex-start',
        gap: 4,
        flexWrap: 'wrap',
    },

    infoTextStrong: {
        fontWeight: 700 as any,
        fontSize: 20,
        lineHeight: '30px',
    },

    infoTextSuffix: {
        display: 'block',
        flex: '0 1 auto',
    },

    infoTextLink: {
        fontFamily: '"Circular Std", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 700 as any,
        textAlign: 'left',
        color: '#94004A',
        fontSize: 18,
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: {
            fontSize: 16,
        },
    },
    infoHighlight: {
        color: '#94004A',
    },

    infoLogo: {
        height: 250,
        width: 'auto',
        flexShrink: 0,
        marginRight: 30,
        [theme.breakpoints.down('sm')]: {
            alignSelf: 'flex-start',
            height: 180,                  // smaller on mobile
            marginTop: 8,                // breathing room under text
            marginRight: 0,
        },
    },
    // optional: smaller radii on very small screens if you want
    [theme.breakpoints.down('sm')]: {
        infoBox: {
            borderRadius: 16,
            padding: '20px 16px',
        },
    },

    skipLink: {
        // spacing: more above, a bit below
        margin: '30px auto 32px',

        // center + size behavior
        display: 'block',
        width: 'max-content',     // let the width fit the text
        maxWidth: '100%',         // don't overflow small screens
        minWidth: 208,            // keep your original spec as a floor
        minHeight: 32,            // keep the visual height
        whiteSpace: 'nowrap',     // keep ">" on the same line

        // typography & visuals
        fontFamily: '"Circular Std", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: 20,
        textAlign: 'center',
        textDecoration: 'underline',
        color: '#94004A',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer'
    },

    infoModalTitle: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px 24px',
    },

    infoModalHeading: {
        flex: 1,
        fontWeight: 700 as any,
    },

    infoModalClose: {
        marginLeft: 8,
        color: '#94004A',
        fontSize: '1.5rem',
        '& svg': {
            fontSize: '2rem',
        },
    },

    infoModalActions: {
        padding: '16px 24px',
    },

    infoModalDetailsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        gap: 16,
        width: '100%',
        marginTop: 24,
    },

    infoModalDetails: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        padding: '24px 32px',
        gap: 12,
        maxWidth: '100%',
        background: '#F7F8F9',
        borderRadius: 20,
        [theme.breakpoints.down('sm')]: {
            padding: '25px',
            alignItems: 'flex-start',
            maxWidth: '100%',
        },
    },

    infoModalDetailsTitle: {
        alignSelf: 'stretch',
        fontWeight: 700 as any,
        fontSize: 18,
        lineHeight: '26px',
        color: '#0F172A',
    },

    infoModalDetailsText: {
        alignSelf: 'stretch',
        fontSize: 16,
        lineHeight: '24px',
        color: '#1F2937',
    },
}));

const ApplicantDepositVerificator: React.FC<Props> = ({ classes, t }) => {

    const s = useLocalStyles()
    const theme = useTheme()
    const isMobileDialog = useMediaQuery(theme.breakpoints.down('sm'))
    const history = useHistory()
    const routeMatch = useRouteMatch() as any
    const applicationCode = routeMatch?.params?.applicationCode
    const applicantCode = useMemo(() => {
        const qs = new URLSearchParams(location.search)
        return qs.get('applicant') || ''
    }, [location.search])
    const [acceptedDepositService, setAcceptedDepositService] = useState(false)
    const [application, setApplication] = useState<any>(null)
    const [apartment, setApartment] = useState<any>(null)
    const [submittingBank, setSubmittingBank] = useState<string | null>(null)
    const depositAmount = useMemo(() => {
        const confirmation = apartment?.confirmation
        if (!confirmation) {
            return {
                value: null as number | null,
                formatted: '',
                monthlyValue: null as number | null,
                monthlyFormatted: ''
            }
        }

        const parseToNumber = (value: unknown): number | null => {
            if (typeof value === 'number' && !Number.isNaN(value)) return value
            if (typeof value === 'string') {
                const sanitized = value
                    .replace(/[^\d,.-]/g, '')
                    .replace(',', '.')
                const parsed = Number(sanitized)
                return Number.isNaN(parsed) ? null : parsed
            }
            return null
        }

        const toDepositShape = (amount: number | null) => {
            if (amount === null) {
                return {
                    value: null as number | null,
                    formatted: '',
                    monthlyValue: null as number | null,
                    monthlyFormatted: ''
                }
            }
            const monthly = amount * 0.01
            return {
                value: amount,
                formatted: numberToEuroString(amount),
                monthlyValue: monthly,
                monthlyFormatted: numberToEuroString(monthly)
            }
        }

        const normalizedAmount = parseToNumber(confirmation.securityDepositAmount)
        if (normalizedAmount !== null) {
            return toDepositShape(normalizedAmount)
        }

        const price = parseToNumber(confirmation.price)
        const depositMultipliers = {
            zero: 0,
            oneMonth: 1,
            twoMonths: 2,
            threeMonths: 3
        } as const

        if (
            price !== null &&
            typeof confirmation.securityDeposit === 'string' &&
            confirmation.securityDeposit in depositMultipliers
        ) {
            const multiplier =
                depositMultipliers[confirmation.securityDeposit as keyof typeof depositMultipliers]
            const calculatedAmount = price * multiplier
            return toDepositShape(calculatedAmount)
        }

        return toDepositShape(null)
    }, [apartment])

    const depositMonthlyAmountText = depositAmount.monthlyFormatted || "-"

    const isBankInteractionDisabled = !acceptedDepositService || submittingBank !== null

    React.useEffect(() => {
        const storedApplication = localStorage.getItem('application')
        if (storedApplication) {
            try {
                setApplication(JSON.parse(storedApplication))
            } catch (error) {
                console.error('Failed to parse stored application', error)
            }
        }

        const storedApartment = localStorage.getItem('apartment')
        if (storedApartment) {
            try {
                setApartment(JSON.parse(storedApartment))
            } catch (error) {
                console.error('Failed to parse stored apartment', error)
            }
        }
    }, [])

    const goToStep5 = () => {
        const applicationData = JSON.parse(localStorage.getItem(application.applicationId) || '{}');
        applicationData.processCompleted = true;
        localStorage.setItem(application.applicationId, JSON.stringify(applicationData));
        //     localStorage removeItem('secondary');
        history.push(`/aftershowing/${applicationCode}/steps/3`)
    }

    const handleBankClick = async (bankId: string) => {
        if (isBankInteractionDisabled) return
        if (!applicationCode || !applicantCode) return

        try {
            setSubmittingBank(bankId)
            const { url } = await ApplicationsApi.createTenantForDigivakuus({
                applicationCode,
                applicantCode,
                bank: bankId,
                acceptedDepositService
            })
            if (url) window.location.assign(url)
            else goToStep5()
        } catch (e) {
            console.error('Error creating tenant for Digivakuus:', e)
            // Keep spinner active; user is expected to navigate away.
        }
    }

    return (
        <>
            <Grid container className={classes.formContainer}>
                <Grid item xs={12}>
                    <Grid container className={classes.marginAuto}>
                        <Grid item xs={12} className={classes.responsiveWidth}>
                            <Grid item xs={12}>
                                <div className={classes.headerStyle}>
                                    {t('afterShowing.depositPageHeader')}
                                </div>
                                <Typography variant='body1' className={classes.authDesc}>
                                    <Trans
                                        i18nKey='afterShowing.depositPageInfo'
                                        values={{ amount: depositMonthlyAmountText }}
                                        components={[<span className={s.infoHighlight} />]}
                                    />
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <div className={s.infoBox}>
                                    {/* Left: fixed checkbox column */}
                                    <div className={s.infoCheckCol}>
                                        <input
                                            name="acceptedDepositService"
                                            type="checkbox"
                                            className={s.nativeCheckbox}
                                            checked={acceptedDepositService}
                                            onChange={(e) => {
                                                const checked = e.target.checked
                                                setAcceptedDepositService(checked)

                                                if (applicantCode) {
                                                    localStorage.setItem(`digivakuusAccepted_${applicantCode}`, JSON.stringify(checked))
                                                }
                                            }}
                                            aria-label={t('afterShowing.digivakuusInfoBold') as string}
                                        />
                                    </div>

                                    {/* Right: text + logo start immediately to the right of checkbox */}
                                    <div className={s.infoRight}>
                                        <div className={s.infoTextWrap}>
                                            <Typography component='div' className={s.infoText}>
                                                <span className={s.infoTextBoldRow}>
                                                    <strong className={s.infoTextStrong}>{t('afterShowing.digivakuusInfoBold')}</strong>
                                                </span>
                                                <DigivakuusFaqLink
                                                    t={t}
                                                    variant='afterShowing'
                                                    depositMonthlyAmountText={depositMonthlyAmountText}
                                                    buttonClassName={s.infoTextLink}
                                                    inlineWrapperClassName={s.infoTextSuffixRow}
                                                    dialogId='digivakuus-info-dialog'
                                                    dialogProps={{
                                                        fullScreen: isMobileDialog
                                                    }}
                                                    dialogTitleProps={{ className: s.infoModalTitle }}
                                                    dialogHeadingClassName={s.infoModalHeading}
                                                    closeIconButtonClassName={s.infoModalClose}
                                                    detailsWrapperClassName={s.infoModalDetailsWrapper}
                                                    detailsClassName={s.infoModalDetails}
                                                    detailsTitleClassName={s.infoModalDetailsTitle}
                                                    detailsTextClassName={s.infoModalDetailsText}
                                                    dialogActionsProps={{ className: s.infoModalActions }}
                                                />
                                            </Typography>
                                        </div>

                                        <img
                                            className={s.infoLogo}
                                            src='https://rentapp-images.s3.eu-central-1.amazonaws.com/Digivakuus_handshake.svg'
                                            alt='Digivakuus'
                                            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                                        />
                                    </div>
                                </div>
                            </Grid>


                            <div className={s.bankGrid}>
                                {banks.map(b => (
                                    <button
                                        key={b.id}
                                        type="button"
                                        className={s.bankCard}
                                        onClick={() => handleBankClick(b.id)}
                                        disabled={isBankInteractionDisabled}
                                        aria-disabled={isBankInteractionDisabled}
                                        aria-busy={submittingBank === b.id}
                                    >
                                        {submittingBank === b.id ? (
                                            <CircularProgress size={32} style={{ color: '#94004A' }} />
                                        ) : (
                                            <img
                                                className={s.bankLogo}
                                                src={b.logo}
                                                alt={b.name}
                                                onError={e => { (e.currentTarget as HTMLImageElement).src = '/fallback-bank.png' }}
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>

                            <button
                                type="button"
                                className={s.skipLink}
                                onClick={goToStep5}
                            >
                                {t?.('afterShowing.skipDepositStep')}
                            </button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default withTranslation()(ApplicantDepositVerificator)
